import React, { useState, useEffect } from 'react';
import {
  FileText,
  ChevronDown,
  Building2,
  Calendar,
  Search,
  RefreshCw,
  IndianRupee,
  FileDown,
  Loader2,
  User,
  Users,
} from 'lucide-react';
import { establishmentAPI, formsAPI, employeeAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';

const Form19Payslip = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [payslips, setPayslips] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeePayroll, setEmployeePayroll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    establishment_id: lockedEstId || '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    search: '',
  });

  // Month/Year options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setFilters(prev => ({ ...prev, establishment_id: lockedEstId }));
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch data on mount
  useEffect(() => {
    if (!lockedEstId) fetchEstablishments();
    fetchEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch payslips when filters change or employees are loaded
  useEffect(() => {
    if (filters.establishment_id && employees.length > 0) {
      fetchPayslips();
    }
  }, [filters.establishment_id, filters.month, filters.year, employees]);

  const fetchEstablishments = async () => {
    try {
      const response = await establishmentAPI.getAll();
      const estabs = response.establishments || [];
      setEstablishments(estabs);
      if (estabs.length > 0 && !filters.establishment_id) {
        setFilters(prev => ({ ...prev, establishment_id: estabs[0].id.toString() }));
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError('Error fetching organizations');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll({ limit: 1000 });
      const employeesList = response.employees || response.data || response || [];
      setEmployees(employeesList);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchPayslips = async () => {
    if (!filters.establishment_id) {
      setPayslips([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use formsAPI to fetch Form 19 payslips
      const params = {
        establishment_id: filters.establishment_id,
        form_type: 'form_19',
        month: filters.month,
        year: filters.year
      };

      console.log('📋 Fetching payslips with params:', params);
      console.log('👥 Available employees count:', employees.length);

      const response = await formsAPI.listGeneratedForms(params);
      console.log('✅ API Response:', response);

      const formsList = response.forms || [];
      console.log(`📊 Forms returned from API: ${formsList.length}`);

      if (formsList.length > 0) {
        console.log('📝 Sample form data (first form):', formsList[0]);
      }

      // Filter to only show PDF files (not XLSX) to avoid duplicates
      const pdfForms = formsList.filter(form =>
        form.file_name && form.file_name.toLowerCase().endsWith('.pdf')
      );
      console.log(`📄 PDF forms filtered: ${pdfForms.length}`);

      // Map forms to payslip format with employee details
      const payslipsFromS3 = pdfForms.map((form, index) => {
        const employee = employees.find(emp => emp.employee_id == form.employee_id);

        console.log(`Processing form ${index + 1}/${formsList.length}:`, {
          form_employee_id: form.employee_id,
          employee_found: !!employee,
          employee_name_from_form: form.employee_name,
          employee_name_from_lookup: employee ? `${employee.first_name} ${employee.last_name}` : 'Not found'
        });

        return {
          emp_code: form.employee_id || '',
          emp_name: employee ? `${employee.first_name} ${employee.last_name}` : (form.employee_name || 'Unknown'),
          emp_desig: employee?.designation || 'N/A',
          emp_gross: form.gross_salary || 0,
          emp_net: form.net_salary || 0,
          month: form.month || filters.month,
          year: form.year || filters.year,
          pdf_url: form.s3_url,  // Use s3_url directly from generated_forms table
          generated_at: form.generated_at,
          s3_file: true,
          form_id: form.id
        };
      });

      console.log(`✨ Final payslips array length: ${payslipsFromS3.length}`);
      setPayslips(payslipsFromS3);
    } catch (error) {
      console.error('❌ Error fetching payslips:', error);
      setError('Error fetching payslips: ' + error.message);
      setPayslips([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRefresh = () => {
    fetchPayslips();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getMonthName = (monthNum) => {
    const month = months.find(m => m.value === parseInt(monthNum));
    return month ? month.label : monthNum;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.employee_id == employeeId);
    if (employee) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return 'Unknown Employee';
  };

  // Filter payslips by search
  const filteredPayslips = payslips.filter(payslip => {
    const searchLower = filters.search.toLowerCase();
    return (
      payslip.emp_code?.toLowerCase().includes(searchLower) ||
      payslip.emp_name?.toLowerCase().includes(searchLower) ||
      payslip.emp_desig?.toLowerCase().includes(searchLower)
    );
  });

  const selectedEstablishment = establishments.find(
    est => est.id === parseInt(filters.establishment_id)
  );

  return (
    <div className="new-employee-container">
      {/* Header */}
      <div className="new-employee-header-section">
        <div>
          <h2 className="new-employee-header">
            <FileText size={28} style={{ marginRight: '0.5rem' }} />
            Form 19 - Payslips
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', marginTop: '8px' }}>
            View and download employee payslips
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: 'var(--theme-surface, #ffffff)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}>
          {/* Establishment Filter */}
          {!lockedEstId && (
            <EstablishmentCard
              establishments={establishments}
              value={filters.establishment_id}
              onChange={(e) => handleFilterChange('establishment_id', e.target.value)}
              style={{ marginBottom: 0 }}
            />
          )}

          {/* Month Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem',
            }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Month
            </label>
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem',
            }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Year
            </label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem',
            }}>
              <Search size={16} style={{ display: 'inline', marginRight: '0.25rem' }} />
              Search
            </label>
            <input
              type="text"
              placeholder="Search employee..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <button
            onClick={handleRefresh}
            disabled={!filters.establishment_id || loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1rem',
              background: '#2563eb',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#fff',
              cursor: (!filters.establishment_id || loading) ? 'not-allowed' : 'pointer',
              opacity: (!filters.establishment_id || loading) ? 0.5 : 1,
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1rem',
          color: '#991b1b',
        }}>
          {error}
        </div>
      )}

      {/* Summary Stats */}
      {selectedEstablishment && filteredPayslips.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', opacity: 0.9 }}>
              <Users size={20} />
              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Total Payslips</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700' }}>
              {filteredPayslips.length}
            </div>
            <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.8 }}>
              {getMonthName(filters.month)} {filters.year}
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 4px 6px rgba(245, 87, 108, 0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', opacity: 0.9 }}>
              <Building2 size={20} />
              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Organization</span>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
              {selectedEstablishment.name}
            </div>
            <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.8 }}>
              {selectedEstablishment.address || 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* Payslips Table */}
      <div style={{
        background: 'var(--theme-surface, #ffffff)',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem',
          }}>
            <Loader2 size={32} className="spinner" style={{ color: '#2563eb' }} />
            <span style={{ marginLeft: '0.75rem', color: '#6b7280' }}>Loading payslips...</span>
          </div>
        ) : !filters.establishment_id ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280',
          }}>
            <Building2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
            <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
              Select an Organization
            </p>
            <p style={{ fontSize: '0.875rem' }}>
              Please select an organization to view payslips
            </p>
          </div>
        ) : filteredPayslips.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280',
          }}>
            <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
            <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
              No Payslips Found
            </p>
            <p style={{ fontSize: '0.875rem' }}>
              No payslips available for {getMonthName(filters.month)} {filters.year}
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--theme-surface)', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Employee Name
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Month / Year
                </th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Download
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPayslips.map((payslip, index) => (
                <tr key={`${payslip.emp_code}-${index}`} style={{
                  borderBottom: index < filteredPayslips.length - 1 ? '1px solid #e5e7eb' : 'none',
                  transition: 'background-color 0.15s',
                }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#111827' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <User size={16} style={{ color: '#6b7280' }} />
                      <span style={{ fontWeight: '500' }}>{payslip.emp_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Calendar size={14} style={{ color: '#9ca3af' }} />
                      {getMonthName(payslip.month)} {payslip.year}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        onClick={() => {
                          if (payslip.pdf_url) {
                            window.open(payslip.pdf_url, '_blank');
                          } else {
                            setError('PDF not available for this payslip');
                          }
                        }}
                        disabled={!payslip.pdf_url}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.5rem',
                          background: payslip.pdf_url ? '#dc2626' : '#d1d5db',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: payslip.pdf_url ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s',
                        }}
                        title="Download Payslip PDF"
                        onMouseEnter={(e) => {
                          if (payslip.pdf_url) {
                            e.currentTarget.style.background = '#b91c1c';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (payslip.pdf_url) {
                            e.currentTarget.style.background = '#dc2626';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <FileDown size={18} style={{ color: '#fff' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        tbody tr:hover {
          background-color: #ffffff;
        }
      `}</style>
    </div>
  );
};

export default Form19Payslip;
